import {
	faArrowRight,
	faRotateRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontReorderItems } from '../../../services/portalDashboardService';
import { ItemCarouselSkeleton } from '../../skeletons';
import { ItemCarousel } from './ItemCarousel';

/**
 * Reorder widget — horizontal-scrolling carousel of items the active
 * customer is "due to reorder right now" based on their 12-month
 * invoice history (recency + frequency + non-declining trend).
 *
 * Login-required: anonymous visitors see a sign-in CTA in place of
 * the carousel since the underlying query is per-client. The widget
 * is intentionally NOT auto-hidden on anonymous load — the CTA
 * doubles as a reason to sign up. Self-fetches its data so the parent
 * dashboard doesn't need to know about every widget's payload.
 */
export function ReorderItemsWidget({
	title,
	limit,
	density,
}: {
	title?: string;
	limit?: number;
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();

	const queryKey = ['portal', 'dashboard', 'reorder-items', limit ?? null];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontReorderItems(token, limit),
		enabled: token != null,
		staleTime: 5 * 60_000,
	});

	const items = data?.data ?? [];

	// Auto-hide rules: no useful empty state for this widget. Hide when:
	//  - the visitor isn't signed in (Account widget already carries
	//    the sign-in CTA — duplicating it on every personalized
	//    widget is noise)
	//  - the customer has no qualifying purchase history (Reorder
	//    is a discoverability feature; if it can't suggest anything,
	//    showing "we have nothing to suggest" wastes prime real estate)
	if (token == null) return null;
	if (!isLoading && !isError && items.length === 0) return null;

	return (
		<section className="w-full h-full flex flex-col">
			<header className="flex items-center justify-between mb-3">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon
						icon={faRotateRight}
						className="text-portal-accent"
					/>
					{t(title ?? 'Order again')}
				</h2>
				<button
					type="button"
					onClick={() => navigate('/orders')}
					className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
				>
					{t('View order history')}
					<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
				</button>
			</header>

			{isLoading ? (
				/* Shaped like the item strip below (PD-14692). */
				<ItemCarouselSkeleton />
			) : isError ? (
				<EmptyMessage
					message={t("We couldn't load your reorder picks right now.")}
				/>
			) : (
				<ItemCarousel
					items={items}
					invalidateQueryKey={queryKey}
					density={density}
					emptyState={null}
				/>
			)}
		</section>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}
